import { z } from 'zod';
import { MoodSchema } from './theme';
/**
 * Minigame Manifest（Phase D3）
 *
 * Phase D「ミニゲームデザインシステム」の Phase B 凍結対象 API。
 * 各ミニゲームが「どの mood で・どの Foundation コンポーネントを使い・どの Visual Grammar
 * 制約を満たしているか」を宣言する manifest フォーマット。
 *
 * 設計根拠: `docs/prod/phaseD_minigame_design_system_instructions.md` §5.2。
 *
 * SSoT:
 * - mood enum は `theme.ts` の `MoodSchema` を再利用する（重複定義しない）。mood の追加/
 *   削除は theme.ts 側のみで行い、本 manifest は自動追従する。
 * - Semantic コンポーネント名（Primitives 4 + Semantic 10）はここで一元管理し、Foundation
 *   実装と必ず一致させる。乖離が出た場合は本ファイルを SSoT として扱う。
 *
 * 注意:
 * - Phase B 凍結後はキー名・必須/任意の破壊的変更は不可。値の追加（mood, component）は
 *   minor 変更扱い。
 * - 本スキーマは registry とは独立。manifest 単体で配布可能なドキュメントの役割も持つ。
 */
/**
 * Foundation で公開している実コンポーネント名。
 * - Primitives 4 個（Button / Card / Modal / Icon）
 * - Semantic 10 個（AdvanceTrigger / JudgementBurst / JudgementModal / TensionGauge /
 *   StatusMeter / HintBubble / EvidenceCard / StartGate / FailOverlay / MinigameHud）
 * 合計 14 種。新規追加時は Foundation 実装と同時に値を増やす。
 */
export const SemanticComponentName = z.enum([
    // primitives
    'Button',
    'Card',
    'Modal',
    'Icon',
    // semantic
    'AdvanceTrigger',
    'JudgementBurst',
    'JudgementModal',
    'TensionGauge',
    'StatusMeter',
    'HintBubble',
    'EvidenceCard',
    // semantic Group C（2026-06-12 R1 体験文法統一で追加。enum 値の追加＝凍結ルール上 minor）
    'StartGate',
    'FailOverlay',
    'MinigameHud',
]);
/**
 * Visual Grammar Constraints（設計書 §4.4）。
 * 各ミニゲームが満たすべき視覚文法の宣言。
 * - advanceDirection: 「次へ」進行方向（右 or 下）。
 * - hintPosition: ヒント表示位置の既定。
 * - feedbackCenter: 正解フィードバックを画面中央に出すか。
 * - timerColorStages: 残り 75/50/25% で緑 → 琥珀 → 赤に段階変化させるか。
 */
export const ComplianceSchema = z.object({
    advanceDirection: z.enum(['right', 'down']),
    hintPosition: z.enum(['bottom-right', 'bottom-center', 'top-right']).default('bottom-right'),
    feedbackCenter: z.boolean().default(true),
    timerColorStages: z.boolean().default(true),
});
/**
 * Minigame Manifest 本体。
 * schemaVersion を必ず固定値 1 とし、将来の破壊的変更は version 番号で分岐する。
 */
export const MinigameManifestSchema = z.object({
    schemaVersion: z.literal(1),
    gameId: z.string().min(1),
    displayName: z.string().min(1),
    templateId: z.number().int().positive().optional(),
    mood: z.object({
        primary: MoodSchema,
        tension: MoodSchema.optional(),
        warmth: MoodSchema.optional(),
    }),
    componentsUsed: z.array(SemanticComponentName).min(1),
    identity: z
        .object({
        motif: z.string().optional(),
        customElements: z.array(z.string()).default([]),
    })
        .default({}),
    compliance: ComplianceSchema,
});
//# sourceMappingURL=minigameManifest.js.map